import * as Sentry from "@client/telemetry/sentry";
import { z } from "zod";

import {
  defaultMediaCacheIO,
  legacyMediaCacheKey,
  type MediaCacheIO,
} from "./mediaCacheIO";

export { type MediaCacheIO } from "./mediaCacheIO";

import {
  assignableTrackSchema,
  isAssignableTrack,
  trackNames,
} from "../../../lib/types";
import {
  type GetUserVideoStateResponse,
  type ProgressRow,
} from "../../../lib/videoStateTypes";
import { canonicalVideoKind } from "../../../lib/videoKinds";
import { type BookmarkPayload } from "./videoSync/bookmarkSync";
import { type PlaylistPayload } from "./videoSync/playlistSync";
import { type ProgressPayload } from "./videoSync/progressSync";

// Monotonic ISO timestamp. Two synchronous calls in the same millisecond
// would otherwise produce identical strings, which collides with the
// server's strict-> LWW check: when `flushWatchProgress` bumps a row's
// `clientUpdatedAt` to T and the immediately-following
// `removeFromPlaylist` enqueues a `playlistOrder` write also at T, the
// playlist write is rejected (existing T >= incoming T) and the
// removal never syncs to other devices.
let lastNowIsoMs = 0;

function nowIso(): string {
  const now = Math.max(Date.now(), lastNowIsoMs + 1);
  lastNowIsoMs = now;
  return new Date(now).toISOString();
}

const storageKey = "mobileVideos.v3";

const mobileChapterSchema = z.object({
  startTimestamp: z.string(),
  title: z.string(),
  summary: z.string(),
});
export type MobileChapter = z.infer<typeof mobileChapterSchema>;

// Track is now a hardcoded domain enum value (nullable when unassigned).
const mobileVideoTrackSchema = assignableTrackSchema;

// Tolerate caches written before `track` became a hardcoded enum: older blobs
// stored it as a `{ id, name }` object. Coerce those to the matching enum (by
// id or display name) or null, so a single legacy video doesn't fail the whole
// persisted store's parse and wipe the user's downloads/progress on upgrade.
function coerceMobileVideoTrack(value: unknown): unknown {
  if (value == null || typeof value === "string") {
    return value;
  }

  if (typeof value !== "object") {
    return value;
  }

  const legacy = value as { id?: unknown; name?: unknown };
  const id = typeof legacy.id === "string" ? legacy.id : null;

  if (isAssignableTrack(id)) {
    return id;
  }

  if (typeof legacy.name === "string") {
    return (
      assignableTrackSchema.options.find(
        (track) => trackNames[track] === legacy.name,
      ) ?? null
    );
  }

  return null;
}

// Tolerate caches written before three kinds were renamed. The persisted
// catalog is written by whatever build was installed at the time, so an app that
// upgrades before refreshing still holds the old labels — and every kind
// predicate would read them as unrecognised, which for a saved recommender
// overview means losing its rail-only home and linking to a track page it cannot
// resolve from. Same shape as coerceMobileVideoTrack: fix it up on the way in
// rather than teaching every consumer about the old names.
function coerceMobileVideoType(value: unknown): unknown {
  return typeof value === "string" ? canonicalVideoKind(value) : value;
}

// Lifecycle:
//   queued → running → completed
//                   ↘ failed (engine schedules retry while toggle is on)
//                   ↘ paused (auto when offline; → running on reconnect)
const downloadStatusSchema = z.enum([
  "queued",
  "running",
  "paused",
  "completed",
  "failed",
]);

const mobileVideoDownloadSchema = z.object({
  status: downloadStatusSchema,
  // NOTE: live download progress (0..1) is NOT here — it lives in
  // downloadProgress.ts, off the durable store, so ~10/s progress ticks don't
  // re-render every store subscriber. See that file for the rationale.
  enqueuedAtIso: z.string(),
  startedAtIso: z.string().nullable(),
  completedAtIso: z.string().nullable(),
  filePath: z.string().nullable(),
  attempts: z.number(),
  lastError: z.string().nullable(),
  // Set when a download failed because the device is out of storage. Such an
  // item is left `failed` with NO retry timer and MUST be treated as
  // non-runnable — syncWithCatalog skips re-enqueuing it — so it doesn't
  // tight-retry against a full disk. Cleared on engine start (one fresh
  // attempt per launch) and by any fresh enqueue (toggle off→on). Optional so
  // records written before this field parse as not-blocked.
  retryBlocked: z.boolean().optional(),
  // True while this item is handed to the native downloader but waiting for
  // one of its transfer slots — the patched plugin caps concurrent transfers
  // (see patches/README.md) and resumes queued tasks itself as slots free.
  // "running" means "handed off" (the whole queue goes native up front), so
  // this flag is what distinguishes "actually transferring" from "natively
  // queued" in the UI. Stamped from download()'s resolve state; cleared by
  // the first progress event after the native promotion (the only JS-visible
  // signal that it happened). Optional so records from before the cap parse;
  // absent = treated as transferring, the pre-cap behavior.
  awaitingNativeSlot: z.boolean().optional(),
  // Local path (under Directory.Documents) of the cached poster image, and
  // the activePosterId it was cached for (so a changed active poster triggers
  // a re-cache). Null until the poster has been downloaded for offline use.
  posterFilePath: z.string().nullable().default(null),
  cachedPosterId: z.string().nullable().default(null),
  // Media file generation these bytes were downloaded from. Lets playback
  // detect a download that predates a media replacement without comparing
  // client and server clocks. Absent on pre-existing records.
  fileUploadedAt: z.string().nullable().optional(),
  // Size and content type of the file the signed download URL actually
  // served — the 720p rendition when one exists, else the original. The
  // catalog's sizeBytes/contentType always describe the original, so
  // rendition downloads must verify and play against these stamps instead
  // (see downloadVerification.ts). Absent on records from before rendition
  // downloads (those fetched the original); null = unknown.
  expectedSizeBytes: z.number().nullable().optional(),
  contentType: z.string().nullable().optional(),
});

const mobileVideoSchema = z.object({
  id: z.string(),
  roundId: z.string(),
  type: z.preprocess(coerceMobileVideoType, z.string()),
  // Older offline catalogs predate round-level display metadata.
  usesTracks: z.boolean().optional(),
  transcriptId: z.string().nullable().default(null),
  transcriptContent: z.string().nullable().default(null),
  // Cached chapters for offline use. `null` = not fetched yet; `[]` = fetched,
  // none exist; non-empty = has chapters. The not-fetched/none distinction is
  // what lets the download engine's backfill know what is still pending.
  chapters: z.array(mobileChapterSchema).nullable().default(null),
  track: z.preprocess(
    coerceMobileVideoTrack,
    mobileVideoTrackSchema.nullable(),
  ),
  // The recommender whose page is this video's canonical home — set by the
  // server for rail-only kinds only (see getVideos). Defaults to null so rows
  // persisted before the field existed still parse.
  homeRecommenderId: z.string().nullable().default(null),
  uploadedAt: z.string(),
  title: z.string(),
  description: z.string(),
  filename: z.string(),
  contentType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  // Size an offline download of this video will actually transfer (the 720p
  // rendition when the server knows one, else the original upload) — what
  // download size DISPLAYS should sum before a per-download served-size stamp
  // exists, so the aggregate total doesn't shrink as stamps arrive. 0 or
  // absent = unknown (rows cached before the field existed); verification
  // still uses expectedSizeBytes/sizeBytes only.
  downloadSizeBytes: z.number().nullable().optional(),
  durationSeconds: z.number().nullable(),
  // Set while `durationSeconds` was learned from local playback but the
  // server report hasn't been confirmed. Cleared on report success or when
  // a sync brings a server-known duration; persisted so offline heals retry
  // on a later online playback.
  durationSecondsPendingReport: z.boolean().optional(),
  // Media file generation this row describes (see reportVideoDuration).
  // Null for legacy uploads; optional so rows cached before the field
  // existed stay valid.
  fileUploadedAt: z.string().nullable().optional(),
  // Seconds to add to a transcript-time position to get the video-time
  // position. Used to play a saved clip as a bounded sub-range of its
  // parent video.
  transcriptToVideoOffsetSeconds: z.number().default(0),
  attendeeCount: z.number().nullable().default(null),
  // Active poster id from the catalog. Stable + persistable; used to build the
  // online poster URL and to detect when a cached poster is stale.
  activePosterId: z.string().nullable().default(null),
  isAvailable: z.boolean(),
  // Set when a round catalog sync no longer includes this video (deleted
  // server-side). The metadata is kept so queue/recent/saved rows can still
  // render the entry and tell the user why it can't play, instead of the row
  // silently vanishing while its id lingers in playlist/bookmarks.
  removedFromCatalog: z.boolean().optional(),
  download: mobileVideoDownloadSchema.nullable(),
  watchProgress: z.number().min(0).max(1).default(0),
  // Per-row LWW timestamp for the local progress slice. Lets hydration
  // skip overwriting a fresher local value with a stale server row.
  progressClientUpdatedAt: z.string().nullable().default(null),
});

const mobileVideoStoreStateSchema = z.object({
  items: z.record(z.string(), mobileVideoSchema),
  order: z.array(z.string()),
  // Round ids the user has toggled "download all" on. Per-round so a
  // multi-round device (iPad) tracks each round independently; on iPhone
  // this holds at most the single hardcoded round.
  downloadAllRoundIds: z.array(z.string()).default([]),
  playlist: z.array(z.string()),
  // Bookmarked videos. Unlike `playlist`, entries here are only ever
  // removed by an explicit user action — completing playback does not
  // clear them.
  bookmarked: z.array(z.string()).default([]),
  // Per-bookmark LWW timestamp keyed by videoId.
  bookmarkClientUpdatedAt: z.record(z.string(), z.string()).default({}),
  // Per-user LWW timestamp for the whole playlist slice.
  playlistClientUpdatedAt: z.string().nullable().default(null),
  wiping: z.boolean().default(false),
  // Ids whose media side record still needs deleting: a round wipe clears
  // rows but keeps them listed, and its side-record deletes only run after
  // the cleared blob is durable — this list rides in that same blob write,
  // so an interrupted cleanup is finished by the next hydrate instead of
  // the stale record rejoining the cleared row. A fresh cache for a listed
  // id (re-download, or viewing the video again) unlists it.
  pendingMediaCacheDeletes: z.array(z.string()).default([]),
});

export type MobileVideoTrack = z.infer<typeof mobileVideoTrackSchema>;
export type MobileVideoDownload = z.infer<typeof mobileVideoDownloadSchema>;
export type MobileVideo = z.infer<typeof mobileVideoSchema>;
export type MobileVideoStoreState = z.infer<typeof mobileVideoStoreStateSchema>;

// Single predicate for "this video was deleted server-side", shared by row
// components, queue auto-advance, and the offline catalog filter so the
// playability rule can't drift between surfaces.
export function isRemovedFromCatalog(video: MobileVideo): boolean {
  return video.removedFromCatalog === true;
}

// Single predicate for "this download holds one of the native transfer slots",
// i.e. it is actually moving bytes right now.
//
// "running" alone does NOT mean that: the engine hands a round's whole queue
// to the background URLSession up front, so on a large round nearly every
// record is "running" while the patched plugin transfers at most 5 of them and
// holds the rest suspended (`awaitingNativeSlot`, see the field's comment on
// mobileVideoDownloadSchema). Anything that acts per actually-transferring
// download — the Downloads dialog's Downloading/Queued split, the navigation
// hold's suspend sweep — must ask THIS, or it does work proportional to the
// whole round instead of to the five live transfers.
//
// An absent flag means a record written before the concurrency cap shipped,
// when every handed-off task did transfer; treating it as transferring keeps
// that behavior.
export function isActivelyDownloading(
  download: MobileVideoDownload | null | undefined,
): boolean {
  return download?.status === "running" && download.awaitingNativeSlot !== true;
}

type Listener = (state: MobileVideoStoreState) => void;

export interface SyncFeatureBridges {
  enqueueProgress: (payload: ProgressPayload) => void;
  enqueueBookmark: (payload: BookmarkPayload) => void;
  enqueuePlaylist: (payload: PlaylistPayload) => void;
}

function emptyState(): MobileVideoStoreState {
  return {
    items: {},
    order: [],
    downloadAllRoundIds: [],
    playlist: [],
    bookmarked: [],
    bookmarkClientUpdatedAt: {},
    playlistClientUpdatedAt: null,
    wiping: false,
    pendingMediaCacheDeletes: [],
  };
}

function loadFromStorage(): MobileVideoStoreState {
  // Read the current v3 blob and return empty state otherwise.
  const raw = localStorage.getItem(storageKey);

  if (!raw) {
    return emptyState();
  }

  try {
    const json = JSON.parse(raw);
    const parsed = mobileVideoStoreStateSchema.parse(json);
    const { items, downloadAllRoundIds } = parsed;
    const order = parsed.order.filter((id) => id in items);

    for (const id of Object.keys(items)) {
      if (!order.includes(id)) {
        order.push(id);
      }
    }

    // Keep playlist/bookmark membership even for ids not yet in `items`: the
    // catalog loads asynchronously, so filtering here would drop entries that
    // hydration stored before the catalog arrived (the order/membership still
    // points at real server videos; the getters skip unknown ids until the
    // catalog fills them in).
    const playlist = parsed.playlist;
    const bookmarked = parsed.bookmarked;

    return {
      items,
      order,
      downloadAllRoundIds,
      playlist,
      bookmarked,
      bookmarkClientUpdatedAt: parsed.bookmarkClientUpdatedAt,
      playlistClientUpdatedAt: parsed.playlistClientUpdatedAt,
      // Always start unlocked; a killed-mid-wipe app should not freeze the
      // toggle forever.
      wiping: false,
      pendingMediaCacheDeletes: parsed.pendingMediaCacheDeletes,
    };
  } catch {
    return emptyState();
  }
}

// Cached transcript text and chapters live OUTSIDE the main blob, in one
// side record per video (written once when the content is cached). The main
// blob is re-serialized on every persisted commit; with a round's worth of
// transcripts inline that was a multi-MB synchronous JSON.stringify +
// localStorage write on the WebView main thread, repeated for every download
// status change. Losing a side record degrades safely: the field hydrates as
// null and the download engine's backfill refetches it.
const mediaCacheSchema = z.object({
  transcriptContent: z.string().nullable().default(null),
  chapters: z.array(mobileChapterSchema).nullable().default(null),
});
type MediaCacheRecord = z.infer<typeof mediaCacheSchema>;

// The records themselves live behind MediaCacheIO (per-video native files on
// device, localStorage on web — see mediaCacheIO.ts for why localStorage's
// quota disqualifies it on device). These legacy helpers read/clear the
// PRE-MIGRATION localStorage location only, so a native device's existing
// records can be moved into files on hydrate — deleting each migrated key is
// what actually drains the exhausted quota.
function readLegacyMediaCache(id: string): MediaCacheRecord | null {
  try {
    const raw = localStorage.getItem(legacyMediaCacheKey(id));

    if (!raw) {
      return null;
    }

    return mediaCacheSchema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

function removeLegacyMediaCache(id: string): void {
  try {
    localStorage.removeItem(legacyMediaCacheKey(id));
  } catch {
    // A leftover legacy record is quota not reclaimed, never a correctness
    // problem: file records are consulted first once one exists.
  }
}

// A persist failure (typically quota) must never throw out of a store
// commit — the in-memory update already happened and the UI depends on it.
// Report once per session; every persisted commit writes the full state, so
// the next successful write self-heals.
let persistFailureReported = false;

function reportPersistFailureOnce(error: unknown): void {
  console.warn("[mobile-video-store] failed to persist to localStorage", {
    error,
  });

  if (persistFailureReported) {
    return;
  }

  persistFailureReported = true;
  Sentry.captureException(error);
}

// The persisted representation of the main blob: media content nulled out
// for items whose side record is known durable (`sideRecorded`). An item
// whose side write failed keeps its content inline — bigger blob, but never
// a destroyed copy. In-memory state always keeps the content; only what
// hits localStorage is stripped.
function stripMediaContent(
  state: MobileVideoStoreState,
  sideRecorded: ReadonlySet<string>,
): MobileVideoStoreState {
  let changed = false;
  const items: Record<string, MobileVideo> = {};

  for (const [id, item] of Object.entries(state.items)) {
    if (
      (item.transcriptContent == null && item.chapters == null) ||
      !sideRecorded.has(id)
    ) {
      items[id] = item;
      continue;
    }

    items[id] = { ...item, transcriptContent: null, chapters: null };
    changed = true;
  }

  return changed ? { ...state, items } : state;
}

// The quota-degradation ladder's strip (S-PROCESS-FRONTEND-8C). This is a
// DELIBERATE exception to the licensed-only rule above: it nulls media
// content for EVERY item, including unlicensed inline copies that are the
// only durable copy. Under quota the triage priorities invert — transcript
// and chapter content is a re-fetchable cache, while the rest of the blob
// (download records, watch progress, pending state) is not, and losing the
// blob wholesale is what actually destroys state (Jaan's device relaunching
// with its downloads forgotten). Hydrate reads a shed item as blob-null +
// no side record = "refetch"; the ladder must never mint a side-record
// license to pretend otherwise.
function stripAllMediaContent(
  state: MobileVideoStoreState,
): MobileVideoStoreState {
  let changed = false;
  const items: Record<string, MobileVideo> = {};

  for (const [id, item] of Object.entries(state.items)) {
    if (item.transcriptContent == null && item.chapters == null) {
      items[id] = item;
      continue;
    }

    items[id] = { ...item, transcriptContent: null, chapters: null };
    changed = true;
  }

  return changed ? { ...state, items } : state;
}

// Distinct one-shot report from reportPersistFailureOnce, so Sentry can tell
// a degraded-but-recovered session (critical state durable, content shed)
// from a truly failed one (nothing landed).
let quotaDegradationReported = false;

function reportQuotaDegradationOnce(
  fullBytes: number,
  strippedBytes: number,
): void {
  console.warn("[mobile-video-store] quota-degraded persist: media shed", {
    fullBytes,
    strippedBytes,
  });

  if (quotaDegradationReported) {
    return;
  }

  quotaDegradationReported = true;
  Sentry.captureMessage(
    `mobile-video-store: quota-degraded persist (full=${fullBytes}B stripped=${strippedBytes}B)`,
    "warning",
  );
}

// The per-store latch persist() consults and updates: once a session has
// quota-degraded, persist stripped-first — retrying the full blob on every
// commit would serialize the oversized payload just to fail again on every
// watch-progress tick. Store-instance state (not module state) so test
// processes, which build many stores, don't leak degradation across files.
// Session-scoped on purpose — the next launch retries full persists, and
// once side records live in native files (SP-2623) the blob carries no
// media content and this ladder goes dormant, the intended end state.
export type PersistQuotaLatch = { degraded: boolean };

// Returns whether the blob durably landed — destructive follow-ups (like
// deleting a removed item's side record) must be gated on this, or a failed
// write leaves the old blob resurrecting rows whose side data is gone.
function persist(
  state: MobileVideoStoreState,
  sideRecorded: ReadonlySet<string>,
  latch: PersistQuotaLatch,
): boolean {
  let fullBytes = 0;

  if (!latch.degraded) {
    const payload = JSON.stringify(stripMediaContent(state, sideRecorded));
    fullBytes = payload.length;

    try {
      localStorage.setItem(storageKey, payload);
      return true;
    } catch {
      // Fall through to the degradation ladder below. Any failure (not just
      // a provable quota error) gets one stripped retry — it is cheap, and
      // a wrong guess costs nothing: the retry either lands or reports.
    }
  }

  const stripped = JSON.stringify(stripAllMediaContent(state));

  try {
    localStorage.setItem(storageKey, stripped);
    latch.degraded = true;
    reportQuotaDegradationOnce(fullBytes, stripped.length);
    return true;
  } catch (error) {
    reportPersistFailureOnce(error);
    return false;
  }
}

// Whether a cached duration may survive a merge: only for the file
// generation it was learned from, or newer — a replaced media file must not
// inherit an older file's value. A row with NO generation recorded (pre-upgrade cache) keeps
// its duration only while it's a pending local heal; local fills always set
// that flag, so it's the one legitimate way such a row can know a duration
// the server lacks. Otherwise a server-null against a cached duration means
// the media was replaced, and keeping it would suppress the heal forever.
export function keepCachedDuration(
  cached: Pick<MobileVideo, "fileUploadedAt" | "durationSecondsPendingReport">,
  incomingFileUploadedAt: string | null | undefined,
): boolean {
  if (cached.fileUploadedAt === undefined) {
    return cached.durationSecondsPendingReport === true;
  }

  const incoming = incomingFileUploadedAt ?? null;

  if (cached.fileUploadedAt === null || incoming === null) {
    return cached.fileUploadedAt === incoming;
  }

  // Same-or-newer: a fill stamped from the signed-URL response can outrun a
  // stale catalog row. Generations are server timestamps, so ISO ordering
  // is safe; only an OLDER value (replaced media) must be dropped.
  return cached.fileUploadedAt >= incoming;
}

// When a kept learned duration carries MORE generation knowledge than the
// incoming row — stamped newer (signed-URL fill against a stale catalog),
// or known-null against a row missing the field entirely — the stamp must
// survive the merge too. Downgrading it would drop the value on the next
// sync: unknown must not erase known.
export function keptNewerStamp(
  cached: Pick<MobileVideo, "fileUploadedAt" | "durationSecondsPendingReport">,
  incomingFileUploadedAt: string | null | undefined,
): { fileUploadedAt: string | null } | Record<string, never> {
  if (!keepCachedDuration(cached, incomingFileUploadedAt)) {
    return {};
  }

  if (
    typeof cached.fileUploadedAt === "string" &&
    (incomingFileUploadedAt == null ||
      cached.fileUploadedAt > incomingFileUploadedAt)
  ) {
    return { fileUploadedAt: cached.fileUploadedAt };
  }

  if (cached.fileUploadedAt === null && incomingFileUploadedAt === undefined) {
    return { fileUploadedAt: null };
  }

  return {};
}

function shallowEqualVideo(a: MobileVideo, b: MobileVideo): boolean {
  const keys = Object.keys(a) as (keyof MobileVideo)[];

  if (keys.length !== Object.keys(b).length) {
    return false;
  }

  for (const key of keys) {
    if (a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}

function chaptersEqual(
  a: MobileChapter[] | null,
  b: MobileChapter[] | null,
): boolean {
  if (a === b) {
    return true;
  }

  if (a == null || b == null || a.length !== b.length) {
    return false;
  }

  return a.every((chapter, index) => {
    const other = b[index];

    return (
      chapter.startTimestamp === other.startTimestamp &&
      chapter.title === other.title &&
      chapter.summary === other.summary
    );
  });
}

function newDownload(nowIso: string): MobileVideoDownload {
  return {
    status: "queued",
    enqueuedAtIso: nowIso,
    startedAtIso: null,
    completedAtIso: null,
    filePath: null,
    attempts: 0,
    lastError: null,
    posterFilePath: null,
    cachedPosterId: null,
  };
}

function isNewer(incoming: string, existing: string | null): boolean {
  if (existing == null) {
    return true;
  }

  return new Date(incoming).getTime() > new Date(existing).getTime();
}

export class MobileVideoStore {
  private state: MobileVideoStoreState = emptyState();
  private readonly listeners = new Set<Listener>();
  private bridges: SyncFeatureBridges | null = null;
  private readonly shouldPersist: boolean;
  // Server progress rows whose fraction can't be computed yet — the video
  // isn't in `items` or its duration is unknown — kept until the catalog
  // fills the gap (drained in syncCatalogMetadata). This makes hydration
  // order-independent, mirroring how bookmarks and the playlist already
  // survive the hydrate-before-catalog race. In-memory only: a reload
  // re-hydrates from the server anyway.
  private readonly pendingProgress = new Map<string, ProgressRow>();
  // Ids whose transcript/chapters side record is known durable on disk, so
  // persist() may strip their inline copy from the main blob. Rebuilt on
  // hydrate; updated on every side-record write. An id absent here keeps its
  // media inline — the safe fallback when a side write failed.
  private readonly mediaSideRecorded = new Set<string>();
  private readonly mediaCacheIO: MediaCacheIO;
  // Per-id chain of in-flight MediaCacheIO operations. The IO is async, so
  // without this a remove could interleave with a write for the same id and
  // leave the record in either order's final state; chaining per id restores
  // the last-call-wins guarantee the synchronous localStorage code had.
  private readonly mediaOps = new Map<string, Promise<unknown>>();
  // Generation identity for pendingMediaCacheDeletes entries. The persisted
  // list stores bare ids, so an id alone cannot distinguish WHICH wipe
  // listed it — and a cleanup that finished for an OLD generation must not
  // unlist a NEWER wipe's instruction for the same id (the only durable
  // order to delete a re-cached record). Each wipe stamps its listings with
  // a per-call token; an unlist only proceeds while the entry still belongs
  // to the generation that did the deleting. Entries hydrated from disk
  // carry no stamp (the pre-launch generation). In-memory only by design:
  // a relaunch starts a fresh generation space and hydrate treats every
  // loaded entry as pre-launch.
  private readonly pendingDeleteStamps = new Map<string, object>();
  // See PersistQuotaLatch: stripped-first persists after a quota degradation,
  // for the rest of this store's life.
  private readonly persistQuotaLatch: PersistQuotaLatch = { degraded: false };

  constructor(
    options: { persist?: boolean; mediaCacheIO?: MediaCacheIO } = {},
  ) {
    this.shouldPersist = options.persist ?? true;
    this.mediaCacheIO = options.mediaCacheIO ?? defaultMediaCacheIO();
  }

  // Runs `op` after every already-queued operation for this id. The returned
  // promise carries op's own result/rejection; the internal chain swallows
  // rejections so one failed operation never poisons the id's queue.
  private queueMediaOp<T>(id: string, op: () => Promise<T>): Promise<T> {
    const prev = this.mediaOps.get(id) ?? Promise.resolve();
    const run = prev.then(op);
    const settled = run.then(
      () => {},
      () => {},
    );
    this.mediaOps.set(id, settled);
    void settled.then(() => {
      if (this.mediaOps.get(id) === settled) {
        this.mediaOps.delete(id);
      }
    });
    return run;
  }

  /** Settles when no MediaCacheIO operation is in flight or queued. For
   * tests and shutdown ordering; product code never needs to wait. */
  async whenMediaCacheIdle(): Promise<void> {
    while (this.mediaOps.size > 0) {
      await Promise.all(this.mediaOps.values());
    }
  }

  private async readSideRecord(id: string): Promise<MediaCacheRecord | null> {
    try {
      // Skip the read when the IO can say there is nothing to read. On native
      // that turns a guaranteed-miss bridge round trip per video — the whole
      // round's worth, on every fresh install — into one directory listing.
      // `null` means the IO doesn't know, so fall through and read.
      if ((await this.mediaCacheIO.hasRecord?.(id)) === false) {
        return null;
      }

      const raw = await this.mediaCacheIO.read(id);

      if (!raw) {
        return null;
      }

      return mediaCacheSchema.parse(JSON.parse(raw));
    } catch {
      // Unreadable = absent: the download engine's backfill refetches.
      return null;
    }
  }

  // Returns whether the record durably landed. Callers gate stripping the
  // inline copy out of the main blob on this: a failed side write (quota,
  // disk full) must leave the inline copy in place or the only durable copy
  // of the content is destroyed.
  private async writeSideRecord(
    id: string,
    record: MediaCacheRecord,
  ): Promise<boolean> {
    try {
      await this.mediaCacheIO.write(id, JSON.stringify(record));
      return true;
    } catch (error) {
      reportPersistFailureOnce(error);
      return false;
    }
  }

  private async removeSideRecord(id: string): Promise<boolean> {
    try {
      await this.mediaCacheIO.remove(id);
      return true;
    } catch {
      // The caller decides whether a leftover record matters: for a removed
      // item it's an orphan (harmless); for a wiped-but-still-listed row it
      // must stay on the pending-deletes list so hydrate finishes the job.
      return false;
    }
  }

  // The legacy localStorage location is only a MIGRATION SOURCE when the
  // active IO stores records somewhere else (native files). The non-native
  // default IO stores AT the legacy keys, and treating them as legacy there
  // would read back the record just written and then delete it — destroying
  // the only durable copy. These wrappers make the legacy path inert in
  // that case.
  private readLegacyForMigration(id: string): MediaCacheRecord | null {
    if (this.mediaCacheIO.storesInLegacyLocation === true) {
      return null;
    }

    return readLegacyMediaCache(id);
  }

  private dropLegacyCopy(id: string): void {
    if (this.mediaCacheIO.storesInLegacyLocation === true) {
      return;
    }

    removeLegacyMediaCache(id);
  }

  // The single supersede rule for everything hydrate does on an id's behalf
  // (its queued migration/repair write, and the final media merge): the id
  // still exists, is not being wiped (every cleared id rides
  // pendingMediaCacheDeletes in the wipe's own commit — the only signal a
  // wipe leaves, since it nulls media to values indistinguishable from a
  // stripped baseline), and its media fields still hold the values hydrate
  // loaded. Anything else means another writer owns the id now — its own
  // commit manages the side record and license.
  private mediaMatchesLoadedBaseline(
    id: string,
    loaded: MobileVideoStoreState,
  ): boolean {
    const cur = this.state.items[id];
    const base = loaded.items[id];

    if (cur == null || base == null) {
      return false;
    }

    if (this.state.pendingMediaCacheDeletes.includes(id)) {
      return false;
    }

    return (
      cur.transcriptContent === base.transcriptContent &&
      chaptersEqual(cur.chapters, base.chapters)
    );
  }

  setSyncFeatures(bridges: SyncFeatureBridges | null): void {
    this.bridges = bridges;
  }

  // Media (transcript/chapters) updates follow a strict write order so the
  // side record can never get AHEAD of the durable blob — a fresher side
  // record beside stale inline data is unresolvable at hydrate (neither
  // copy carries a version) and one of them gets destroyed:
  //   1. revoke the strip license, so the commit persists the new content
  //      INLINE — the update is durable in the blob first;
  //   2. only after that persist confirms, write the side record;
  //   3. only after the side write confirms, re-persist to strip the inline
  //      copy (blob and side agree at that moment).
  // A failure at any step leaves the previous step's copy authoritative;
  // the worst outcome is a bigger blob, never a destroyed or reverted copy.
  private commitMediaUpdate(id: string, next: MobileVideo): void {
    this.mediaSideRecorded.delete(id);

    const persisted = this.commit({
      ...this.state,
      items: { ...this.state.items, [id]: next },
      // A fresh cache supersedes any deferred wipe cleanup for this id —
      // leaving it listed would let the next hydrate delete the new record.
      pendingMediaCacheDeletes: this.state.pendingMediaCacheDeletes.filter(
        (pending) => pending !== id,
      ),
    });
    this.pendingDeleteStamps.delete(id);

    if (!this.shouldPersist || !persisted) {
      return;
    }

    const record: MediaCacheRecord = {
      transcriptContent: next.transcriptContent,
      chapters: next.chapters,
    };
    void this.queueMediaOp(id, async () => {
      // A null field here means "nothing known", never an intentional erase
      // (the setters only ever set values; clears go through the wipe path).
      // During the window between hydrate's phase 1 and this id's rejoin,
      // the in-memory item is stripped — a one-field update built from it
      // would emit null over the sibling field's ONLY durable copy in the
      // existing record. Preserve unknown siblings from the record instead.
      let effective = record;

      if (record.transcriptContent == null || record.chapters == null) {
        const existing =
          (await this.readSideRecord(id)) ?? this.readLegacyForMigration(id);

        if (existing != null) {
          effective = {
            transcriptContent:
              record.transcriptContent ?? existing.transcriptContent,
            chapters: record.chapters ?? existing.chapters,
          };
        }
      }

      if (!(await this.writeSideRecord(id, effective))) {
        return;
      }

      // The record now supersedes any pre-migration localStorage copy;
      // dropping that copy is what reclaims the exhausted quota.
      this.dropLegacyCopy(id);

      // The write was queued from a sync commit, so a NEWER media update may
      // have landed while it was in flight (its own queued write follows in
      // this id's chain). Everything below is gated on the live item still
      // holding the CALLER's committed values — anything else means a newer
      // writer owns the id now.
      const current = this.state.items[id];

      if (
        current == null ||
        current.transcriptContent !== record.transcriptContent ||
        !chaptersEqual(current.chapters, record.chapters) ||
        this.state.pendingMediaCacheDeletes.includes(id)
      ) {
        return;
      }

      // A preserved sibling must reach LIVE state too, not just the record:
      // the caller committed a one-field update against a stripped item, so
      // without this the session serves null for a field that is durably
      // cached — offline, there is no refetch to hide that. (Hydrate's merge
      // deliberately won't rejoin it either: this update changed the media
      // fields, so the id fails its loaded-baseline check.)
      if (effective !== record) {
        this.commit({
          ...this.state,
          items: {
            ...this.state.items,
            [id]: {
              ...current,
              transcriptContent:
                current.transcriptContent ?? effective.transcriptContent,
              chapters: current.chapters ?? effective.chapters,
            },
          },
        });
      }

      // License only if the live content is (now) exactly the record just
      // written — a stale license would let persist() destroy the only copy
      // of newer content.
      const settled = this.state.items[id];

      if (
        settled == null ||
        settled.transcriptContent !== effective.transcriptContent ||
        !chaptersEqual(settled.chapters, effective.chapters)
      ) {
        return;
      }

      this.mediaSideRecorded.add(id);
      persist(this.state, this.mediaSideRecorded, this.persistQuotaLatch);
    });
  }

  enqueueWatchProgress(id: string, timestampIso: string = nowIso()): void {
    const item = this.state.items[id];

    if (!item) {
      return;
    }

    const duration = item.durationSeconds;

    // We can't compute seconds without a duration. Skip the enqueue rather
    // than ship a value that would erase existing progress on the server.
    if (duration == null) {
      return;
    }

    const watchedSeconds = duration * item.watchProgress;

    this.commit({
      ...this.state,
      items: {
        ...this.state.items,
        [id]: { ...item, progressClientUpdatedAt: timestampIso },
      },
    });

    this.bridges?.enqueueProgress({
      videoId: id,
      watchedSeconds,
      clientUpdatedAt: timestampIso,
    });
  }

  applyServerProgress(row: ProgressRow): void {
    const item = this.state.items[row.videoId];

    if (!item) {
      return;
    }

    if (!isNewer(row.clientUpdatedAt, item.progressClientUpdatedAt)) {
      return;
    }

    const duration = item.durationSeconds;

    if (duration == null || duration <= 0) {
      return;
    }

    const fraction = Math.max(0, Math.min(1, row.watchedSeconds / duration));

    if (
      fraction === item.watchProgress &&
      row.clientUpdatedAt === item.progressClientUpdatedAt
    ) {
      return;
    }

    this.commit({
      ...this.state,
      items: {
        ...this.state.items,
        [row.videoId]: {
          ...item,
          watchProgress: fraction,
          progressClientUpdatedAt: row.clientUpdatedAt,
        },
      },
    });
  }

  applyServerHydration(server: GetUserVideoStateResponse): void {
    // Progress: per-video LWW.
    let items = this.state.items;
    let itemsChanged = false;

    for (const row of server.progress) {
      const item = items[row.videoId];

      if (
        item != null &&
        !isNewer(row.clientUpdatedAt, item.progressClientUpdatedAt)
      ) {
        continue;
      }

      const duration = item?.durationSeconds;

      if (item == null || duration == null || duration <= 0) {
        // Can't compute a fraction yet; retain the row for the catalog drain.
        // LWW against any already-pending row so out-of-order responses
        // can't replace a fresher pending value with a staler one.
        const pending = this.pendingProgress.get(row.videoId);

        if (
          pending == null ||
          isNewer(row.clientUpdatedAt, pending.clientUpdatedAt)
        ) {
          this.pendingProgress.set(row.videoId, row);
        }

        continue;
      }

      const fraction = Math.max(0, Math.min(1, row.watchedSeconds / duration));

      if (!itemsChanged) {
        items = { ...items };
        itemsChanged = true;
      }

      items[row.videoId] = {
        ...item,
        watchProgress: fraction,
        progressClientUpdatedAt: row.clientUpdatedAt,
      };
    }

    // Bookmarks: per-video LWW. The server sends a row per (user, video)
    // including soft-deleted ones (bookmarked=false), so an un-bookmark from
    // another device wins here — no ambiguity between "never bookmarked" and
    // "removed".
    const bookmarkClientUpdatedAt = { ...this.state.bookmarkClientUpdatedAt };
    const bookmarkedSet = new Set(this.state.bookmarked);

    for (const row of server.bookmarks) {
      if (
        !isNewer(
          row.clientUpdatedAt,
          bookmarkClientUpdatedAt[row.videoId] ?? null,
        )
      ) {
        continue;
      }

      bookmarkClientUpdatedAt[row.videoId] = row.clientUpdatedAt;

      // Record membership even if the catalog hasn't loaded this video into
      // `items` yet. `savedInOrder()` skips unknown ids until the catalog
      // fills them in, so the membership survives the mount-vs-catalog race.
      // Gating on `items[...] != null` here would advance the LWW timestamp
      // without applying the bookmark, and a later hydration would then skip
      // it as already-seen.
      if (row.bookmarked) {
        bookmarkedSet.add(row.videoId);
      } else {
        bookmarkedSet.delete(row.videoId);
      }
    }

    // Playlist: per-user LWW (full replace) keyed on the server's aggregate
    // marker (`playlistClientUpdatedAt` = newest timestamp across all entry
    // rows, including soft-deleted tombstones). This lets an authoritative
    // *empty* playlist (all items removed on another device) win against a
    // stale local non-empty one: the cleared playlist has no active rows, but
    // its tombstones still carry the clear's timestamp.
    let playlist = this.state.playlist;
    let playlistClientUpdatedAt = this.state.playlistClientUpdatedAt;

    if (
      server.playlistClientUpdatedAt != null &&
      isNewer(server.playlistClientUpdatedAt, playlistClientUpdatedAt)
    ) {
      const ordered = [...server.playlist].sort(
        (a, b) => a.position - b.position,
      );
      // Keep the full server order, including ids the catalog hasn't loaded
      // into `items` yet. `playlistInOrder()` skips unknown ids until the
      // catalog fills them in. Filtering here would drop entries while still
      // advancing the marker, so a later hydration would then skip the
      // playlist and leave a fresh device with an empty queue.
      playlist = ordered.map((row) => row.videoId);
      playlistClientUpdatedAt = server.playlistClientUpdatedAt;
    }

    this.commit({
      ...this.state,
      items,
      playlist,
      bookmarked: Array.from(bookmarkedSet),
      bookmarkClientUpdatedAt,
      playlistClientUpdatedAt,
    });
  }

  // Phase 1 (before the first await, so callers that don't wait still see
  // the persisted state synchronously — every non-media field is final at
  // return of the first microtask): load the main blob and adopt it as-is.
  // Phase 2 (awaited): finish interrupted wipe cleanups, rejoin each item's
  // media content from its side record — migrating any pre-migration
  // localStorage record into the MediaCacheIO and deleting the legacy key,
  // which is what drains an exhausted quota — then commit and re-persist.
  // The download engine awaits the whole thing before its backfills, so the
  // backfill never mistakes not-yet-rejoined content for never-fetched.
  async hydrate(): Promise<void> {
    if (!this.shouldPersist) {
      return;
    }

    const loaded = loadFromStorage();
    this.mediaSideRecorded.clear();
    this.state = loaded;

    // ---- Phase 2 (async) ----

    // Finish interrupted wipe cleanups first: these side records belong to
    // cleared rows and must not rejoin them. Ids whose delete fails again
    // stay listed (and are skipped below) until a launch succeeds. The
    // legacy localStorage copy goes regardless — retrying only needs the
    // IO-side record gone.
    const deletedNow = new Set<string>();

    for (const id of loaded.pendingMediaCacheDeletes) {
      this.dropLegacyCopy(id);
      const ok = await this.queueMediaOp(id, async () => {
        // Revalidate at RUN time: a fresh cache landing mid-sweep unlists
        // the id (and queues its write ahead of this delete in the id's
        // chain); this delete's load-generation claim is then stale and
        // must not destroy the new record. A re-wipe meanwhile stamps the
        // id — that newer generation owns its own cleanup.
        if (
          !this.state.pendingMediaCacheDeletes.includes(id) ||
          this.pendingDeleteStamps.has(id)
        ) {
          return false;
        }

        return this.removeSideRecord(id);
      });

      if (ok) {
        deletedNow.add(id);
      }
    }

    const pendingDeletes = new Set(
      loaded.pendingMediaCacheDeletes.filter((id) => !deletedNow.has(id)),
    );

    // Rejoin media content with its item. INLINE values win: content stays
    // inline in the main blob only when it is authoritative — a pre-side-
    // record blob, or a copy deliberately kept because a side write failed
    // (possibly a stale-record update, where the side record holds the OLD
    // value). A stripped blob has null inline fields, so the side record
    // fills them in the normal case.
    const items: Record<string, MobileVideo> = {};
    const licensed = new Set<string>();

    for (const [id, item] of Object.entries(loaded.items)) {
      if (pendingDeletes.has(id)) {
        items[id] = item;
        continue;
      }

      // The IO record is authoritative once one exists; the legacy
      // localStorage record is consulted only as the migration source.
      const ioSide = await this.readSideRecord(id);
      const legacy = ioSide == null ? this.readLegacyForMigration(id) : null;
      const side = ioSide ?? legacy;

      const transcriptContent =
        item.transcriptContent ?? side?.transcriptContent ?? null;
      const chapters = item.chapters ?? side?.chapters ?? null;

      items[id] =
        transcriptContent === item.transcriptContent &&
        chapters === item.chapters
          ? item
          : { ...item, transcriptContent, chapters };

      if (transcriptContent == null && chapters == null) {
        // A legacy record with nothing effective in it is pure dead quota.
        if (legacy != null) {
          this.dropLegacyCopy(id);
        }

        continue;
      }

      // The strip license requires the IO-side record to carry the full
      // effective media — "a record exists" is not enough: one missing or
      // stale field would be destroyed by the re-persist below. Migrate/
      // repair incomplete records; only a confirmed write (or an already-
      // complete record) licenses the strip. On failure (quota, disk) the
      // blob — or the legacy record — keeps the authoritative copy.
      const ioSideIsComplete =
        ioSide != null &&
        ioSide.transcriptContent === transcriptContent &&
        chaptersEqual(ioSide.chapters, chapters);

      if (
        ioSideIsComplete ||
        (await this.queueMediaOp(id, async () => {
          // Revalidate at RUN time, not queue time: this op is appended to
          // the id's chain, so a fresher commitMediaUpdate write queued
          // before it may have already landed and licensed — writing the
          // loaded-baseline record after it would overwrite the fresher
          // file, and the surviving license would let the final commit
          // strip the fresher content's only inline copy. Same rule as the
          // final merge below: proceed only while the current item's media
          // still IS the loaded baseline and the id isn't being wiped.
          if (!this.mediaMatchesLoadedBaseline(id, loaded)) {
            return false;
          }

          return this.writeSideRecord(id, { transcriptContent, chapters });
        }))
      ) {
        licensed.add(id);
        // Only after the IO record durably carries the content may the
        // legacy copy go — it is the durable copy until that moment.
        this.dropLegacyCopy(id);
      }
    }

    // Commit against the CURRENT state: actions may have run during the
    // awaits above. mediaMatchesLoadedBaseline is the one supersede rule
    // (see its comment) — it deliberately compares MEDIA FIELDS, not object
    // identity, so a catalog metadata sync that rebuilds the item object
    // without touching its media still gets the rejoined content merged in
    // (dropping it would strand cached transcripts — refetch online,
    // unavailable offline — on every launch-time catalog race).
    const current = this.state;
    const mergedItems: Record<string, MobileVideo> = { ...current.items };

    for (const [id, rejoined] of Object.entries(items)) {
      const cur = current.items[id];

      if (!this.mediaMatchesLoadedBaseline(id, loaded) || cur == null) {
        // Removed, wiped, or independently updated while hydrating — do not
        // resurrect or clobber, and do not license a record for content
        // that is no longer committed.
        licensed.delete(id);
        continue;
      }

      mergedItems[id] =
        cur.transcriptContent === rejoined.transcriptContent &&
        cur.chapters === rejoined.chapters
          ? cur
          : {
              ...cur,
              transcriptContent: rejoined.transcriptContent,
              chapters: rejoined.chapters,
            };
    }

    for (const id of licensed) {
      this.mediaSideRecorded.add(id);
    }

    // Re-persist under the current v3 key (no v1 migration); this writes
    // forward stale wiping/order normalization, completed cleanup-list
    // entries, and strips migrated media content out of the main blob. This
    // runs through commit() (unlike the pre-async hydrate) so subscribers
    // that rendered phase-1 state learn the media content arrived — and so
    // the write rides the quota ladder like every other persist.
    this.commit({
      ...current,
      items: mergedItems,
      // Unlist only entries still of the LOAD generation (unstamped): a
      // wipe during hydrate re-stamps its id, and its instruction must
      // survive the startup cleanup's bookkeeping.
      pendingMediaCacheDeletes: current.pendingMediaCacheDeletes.filter(
        (id) => !(deletedNow.has(id) && !this.pendingDeleteStamps.has(id)),
      ),
    });
  }

  getState(): MobileVideoStoreState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  // Returns whether this commit durably persisted the blob (false for
  // non-persisting stores, skipped persists, and failed writes) so callers
  // with destructive follow-ups can gate on it.
  private commit(
    next: MobileVideoStoreState,
    options: { skipPersist?: boolean } = {},
  ): boolean {
    this.state = next;

    let persisted = false;

    if (this.shouldPersist && !options.skipPersist) {
      persisted = persist(next, this.mediaSideRecorded, this.persistQuotaLatch);
    }

    for (const listener of this.listeners) {
      listener(next);
    }

    return persisted;
  }

  enqueue(video: MobileVideo, nowIso: string = new Date().toISOString()): void {
    this.enqueueMany([video], nowIso);
  }

  // Queue a whole batch in ONE commit — the shape every bulk mutation here
  // takes (see syncCatalogMetadata). Per-item commits are what "Download all"
  // used to do: every commit re-serializes the entire blob to localStorage and
  // wakes every subscriber, and the toggle paid that once per video (184 of
  // them on the funder's round) synchronously, in the tap that flipped it. The
  // WebView had no frame to spare for the next tap, so the queue drawer
  // ignored taps until the burst drained.
  enqueueMany(
    videos: readonly MobileVideo[],
    nowIso: string = new Date().toISOString(),
  ): void {
    let changed = false;
    const items = { ...this.state.items };
    const order = [...this.state.order];
    const inOrder = new Set(order);

    for (const video of videos) {
      // Read the accumulating map, not this.state.items: an id repeated within
      // one batch must see the previous iteration's record, exactly as
      // successive enqueue() calls did.
      const existing = items[video.id];
      const existingStatus = existing?.download?.status;

      if (
        existingStatus === "queued" ||
        existingStatus === "running" ||
        existingStatus === "paused"
      ) {
        continue;
      }

      items[video.id] = {
        ...existing,
        ...video,
        // enqueue is a download operation — it must never ingest the incoming
        // video's progress pair or wipe device-side caches. While impersonating,
        // the drawer hands the engine videos hydrated from a per-view store:
        // their watchProgress/progressClientUpdatedAt belong to the impersonated
        // user (persisting them would surface a stranger's watch history here
        // and, via LWW, block the real user's own server rows from hydrating),
        // and their transcript/chapter caches are null. So the progress pair is
        // taken atomically from this store — zeroed for new items; the real
        // values arrive via hydration, which a null timestamp never blocks —
        // and existing caches are kept over incoming nulls.
        watchProgress: existing != null ? existing.watchProgress : 0,
        progressClientUpdatedAt:
          existing != null ? existing.progressClientUpdatedAt : null,
        transcriptContent:
          existing?.transcriptContent ?? video.transcriptContent,
        chapters: existing?.chapters ?? video.chapters,
        download: newDownload(nowIso),
      };
      changed = true;

      if (!inOrder.has(video.id)) {
        order.push(video.id);
        inOrder.add(video.id);
      }
    }

    if (!changed) {
      return;
    }

    this.commit({ ...this.state, items, order });
  }

  updateDownload(id: string, patch: Partial<MobileVideoDownload>): void {
    this.updateDownloadMany([{ id, patch }]);
  }

  // The batched form of updateDownload, for a caller stamping a whole handoff
  // at once. Same one-commit-per-batch reasoning as enqueueMany.
  updateDownloadMany(
    updates: readonly { id: string; patch: Partial<MobileVideoDownload> }[],
  ): void {
    let changed = false;
    const items = { ...this.state.items };

    for (const { id, patch } of updates) {
      const existing = items[id];

      if (!existing?.download) {
        continue;
      }

      items[id] = {
        ...existing,
        download: { ...existing.download, ...patch },
      };
      changed = true;
    }

    if (!changed) {
      return;
    }

    // Download updates are all structural changes (status/filePath/error/
    // stamps) — live progress lives off-store in downloadProgress.ts — so this
    // persists. Progress ticks no longer reach the store, which is the whole
    // point: they can't churn the persisted blob or wake subscribers.
    this.commit({ ...this.state, items });
  }

  remove(id: string): void {
    if (!(id in this.state.items)) {
      return;
    }

    const items = { ...this.state.items };
    delete items[id];

    const bookmarkClientUpdatedAt = { ...this.state.bookmarkClientUpdatedAt };
    delete bookmarkClientUpdatedAt[id];

    const persisted = this.commit({
      ...this.state,
      items,
      order: this.state.order.filter((existing) => existing !== id),
      playlist: this.state.playlist.filter((existing) => existing !== id),
      bookmarked: this.state.bookmarked.filter((existing) => existing !== id),
      bookmarkClientUpdatedAt,
    });

    // Drop the side record only after the removal is durably in the blob: a
    // failed persist leaves the OLD blob resurrecting this row on the next
    // launch, and its side record must still exist to serve that row's
    // media. (An interrupted sequence leaves an orphaned record at worst.)
    if (persisted) {
      void this.queueMediaOp(id, async () => {
        await this.removeSideRecord(id);
        this.dropLegacyCopy(id);
      });
    }

    this.mediaSideRecorded.delete(id);
  }

  setRoundDownloadEnabled(roundId: string, enabled: boolean): void {
    const already = this.state.downloadAllRoundIds.includes(roundId);

    if (already === enabled) {
      return;
    }

    const downloadAllRoundIds = enabled
      ? [...this.state.downloadAllRoundIds, roundId]
      : this.state.downloadAllRoundIds.filter((id) => id !== roundId);

    this.commit({ ...this.state, downloadAllRoundIds });
  }

  isRoundDownloadEnabled(roundId: string): boolean {
    return this.state.downloadAllRoundIds.includes(roundId);
  }

  anyRoundDownloadEnabled(): boolean {
    return this.state.downloadAllRoundIds.length > 0;
  }

  setWatchProgress(id: string, fraction: number): void {
    const existing = this.state.items[id];

    if (!existing) {
      return;
    }

    if (!Number.isFinite(fraction)) {
      return;
    }

    const clamped = Math.max(0, Math.min(1, fraction));

    if (clamped === existing.watchProgress) {
      return;
    }

    this.commit({
      ...this.state,
      items: {
        ...this.state.items,
        [id]: { ...existing, watchProgress: clamped },
      },
    });
  }

  // Clear watch progress and sync the reset to the server. Unlike
  // `enqueueWatchProgress` (which derives `watchedSeconds` from duration and
  // skips when duration is null), a reset always ships `watchedSeconds: 0`, so
  // it persists even for videos whose duration isn't known — otherwise the
  // server keeps the old progress and hydration restores the row.
  resetWatchProgress(id: string): void {
    const existing = this.state.items[id];

    if (!existing) {
      return;
    }

    const timestamp = nowIso();

    this.commit({
      ...this.state,
      items: {
        ...this.state.items,
        [id]: {
          ...existing,
          watchProgress: 0,
          progressClientUpdatedAt: timestamp,
        },
      },
    });

    this.bridges?.enqueueProgress({
      videoId: id,
      watchedSeconds: 0,
      clientUpdatedAt: timestamp,
    });
  }

  // Fill-only: record a duration the native player reported for a video the
  // catalog has no duration for (uploads that predate duration capture). A
  // known duration is never overwritten. Also drains any server progress rows
  // that were parked waiting on this duration, so watch progress computed
  // against it lands immediately instead of on the next catalog sync.
  fillDurationSeconds(
    id: string,
    durationSeconds: number,
    // Generation of the row that was playing, when known. Stamping it lets
    // keepCachedDuration keep the value on generation match alone instead of
    // only while the pending flag is set.
    fileUploadedAt?: string | null,
  ): void {
    const existing = this.state.items[id];
    // Mirror the server's bounds (1s–24h) before rounding: sub-second
    // rounds to 0 (which progress math would treat as real) and oversize is
    // junk the server rejects — never store either.
    const withinBounds =
      Number.isFinite(durationSeconds) &&
      durationSeconds >= 1 &&
      durationSeconds <= 24 * 60 * 60;

    if (!existing || existing.durationSeconds != null || !withinBounds) {
      return;
    }

    const rounded = Math.round(durationSeconds);

    const items = {
      ...this.state.items,
      [id]: {
        ...existing,
        durationSeconds: rounded,
        // Stays set until the server report is confirmed (see
        // markDurationReported) so an offline/failed report retries later.
        durationSecondsPendingReport: true,
        ...(fileUploadedAt !== undefined ? { fileUploadedAt } : {}),
      },
    };

    this.drainPendingProgress(items);
    this.commit({ ...this.state, items });
  }

  // The fill-only server report for a locally learned duration succeeded —
  // no future playback needs to re-send it. The acknowledgement carries the
  // generation it was sent for: if the item's stamp has moved on (media
  // re-attached while the report was in flight), the pending flag now
  // belongs to the new generation's heal and must survive.
  markDurationReported(
    id: string,
    reportedFileUploadedAt?: string | null,
  ): void {
    const existing = this.state.items[id];

    if (!existing || existing.durationSecondsPendingReport !== true) {
      return;
    }

    if (existing.fileUploadedAt !== reportedFileUploadedAt) {
      return;
    }

    const { durationSecondsPendingReport: _, ...rest } = existing;
    this.commit({
      ...this.state,
      items: {
        ...this.state.items,
        // An applied fill proves the server row's generation — it matched
        // the reported one (null for legacy rows). Materialize it so later
        // stale syncs keep the healed value on generation match, which an
        // unstamped row would otherwise lose once the pending flag clears.
        [id]: { ...rest, fileUploadedAt: reportedFileUploadedAt ?? null },
      },
    });
  }

  setWiping(value: boolean): void {
    if (this.state.wiping === value) {
      return;
    }

    this.commit({ ...this.state, wiping: value });
  }

  // When `roundId` is given, `videos` is treated as that round's complete
  // catalog: cached items of the round that are missing from it get flagged
  // `removedFromCatalog` (and reappearing ones unflagged), so lists backed by
  // this store can surface server-side deletions instead of offering rows
  // that can no longer play.
  syncCatalogMetadata(videos: MobileVideo[], roundId?: string): void {
    let changed = false;
    const items = { ...this.state.items };
    const order = [...this.state.order];
    const inOrder = new Set(order);

    for (const video of videos) {
      // Keep every synced video addressable via itemsInOrder(): hydration
      // backfills `order` from `items` only at launch, so without this a
      // video cached mid-session stays invisible to the offline list until
      // the next app start.
      if (!inOrder.has(video.id)) {
        order.push(video.id);
        inOrder.add(video.id);
        changed = true;
      }

      const existing = items[video.id];
      // Anything present in the incoming catalog is by definition not
      // removed, whichever merge branch built it.
      const merged: MobileVideo = {
        ...(existing
          ? {
              ...video,
              download: existing.download,
              watchProgress: existing.watchProgress,
              progressClientUpdatedAt: existing.progressClientUpdatedAt,
              transcriptContent:
                existing.transcriptContent ?? video.transcriptContent ?? null,
              // The catalog never carries chapters, so this just keeps any
              // offline cache rather than clobbering it with null.
              chapters: existing.chapters ?? video.chapters ?? null,
              // Server duration wins; otherwise keep one learned from local
              // playback — but only for the same file generation, so a
              // replaced media file can't inherit the old value.
              durationSeconds:
                video.durationSeconds ??
                (keepCachedDuration(existing, video.fileUploadedAt)
                  ? existing.durationSeconds
                  : null) ??
                null,
              ...(video.durationSeconds == null &&
              existing.durationSecondsPendingReport === true &&
              keepCachedDuration(existing, video.fileUploadedAt)
                ? { durationSecondsPendingReport: true }
                : {}),
              ...(video.durationSeconds == null
                ? keptNewerStamp(existing, video.fileUploadedAt)
                : {}),
            }
          : video),
        removedFromCatalog: false,
      };

      if (existing && shallowEqualVideo(existing, merged)) {
        continue;
      }

      items[video.id] = merged;
      changed = true;
    }

    if (roundId != null) {
      const catalogIds = new Set(videos.map((video) => video.id));

      for (const item of Object.values(items)) {
        if (
          item.roundId !== roundId ||
          catalogIds.has(item.id) ||
          item.removedFromCatalog === true
        ) {
          continue;
        }

        items[item.id] = { ...item, removedFromCatalog: true };
        changed = true;
      }
    }

    if (this.drainPendingProgress(items)) {
      changed = true;
    }

    if (!changed) {
      return;
    }

    this.commit({ ...this.state, items, order });
  }

  // Apply retained server progress rows whose video (or duration) the catalog
  // has now supplied. Mutates the passed `items` copy; returns whether any
  // row applied. Rows still missing their video/duration stay pending; rows
  // that lose LWW to fresher local or catalog progress are discarded.
  private drainPendingProgress(items: Record<string, MobileVideo>): boolean {
    let changed = false;

    for (const [videoId, row] of this.pendingProgress) {
      const item = items[videoId];
      const duration = item?.durationSeconds;

      if (item == null || duration == null || duration <= 0) {
        continue;
      }

      this.pendingProgress.delete(videoId);

      if (!isNewer(row.clientUpdatedAt, item.progressClientUpdatedAt)) {
        continue;
      }

      const fraction = Math.max(0, Math.min(1, row.watchedSeconds / duration));

      items[videoId] = {
        ...item,
        watchProgress: fraction,
        progressClientUpdatedAt: row.clientUpdatedAt,
      };
      changed = true;
    }

    return changed;
  }

  addToPlaylist(video: MobileVideo): void {
    if (this.state.playlist.includes(video.id)) {
      return;
    }

    const existing = this.state.items[video.id];
    const merged: MobileVideo = {
      ...video,
      download: existing?.download ?? null,
      watchProgress: existing?.watchProgress ?? video.watchProgress,
      progressClientUpdatedAt:
        existing?.progressClientUpdatedAt ?? video.progressClientUpdatedAt,
      // Don't wipe a playback-learned duration (or its pending-report
      // flag) with the catalog row's null — same-generation rows only.
      durationSeconds:
        video.durationSeconds ??
        (existing != null && keepCachedDuration(existing, video.fileUploadedAt)
          ? existing.durationSeconds
          : null) ??
        null,
      ...(video.durationSeconds == null &&
      existing?.durationSecondsPendingReport === true &&
      existing != null &&
      keepCachedDuration(existing, video.fileUploadedAt)
        ? { durationSecondsPendingReport: true }
        : {}),
      ...(video.durationSeconds == null && existing != null
        ? keptNewerStamp(existing, video.fileUploadedAt)
        : {}),
    };

    const nextPlaylist = [...this.state.playlist, video.id];
    const timestamp = nowIso();

    this.commit({
      ...this.state,
      items: { ...this.state.items, [video.id]: merged },
      playlist: nextPlaylist,
      playlistClientUpdatedAt: timestamp,
    });

    this.bridges?.enqueuePlaylist({
      orderedVideoIds: nextPlaylist,
      clientUpdatedAt: timestamp,
    });
  }

  // Add by id, for callers that hold only a video id (e.g. the funder
  // workspace, which has the round catalog rather than a MobileVideo). Mirrors
  // setVideoBookmarkById: the item details fill in from the catalog sync, and
  // playlistInOrder() skips the id until then.
  addToPlaylistById(videoId: string): void {
    if (this.state.playlist.includes(videoId)) {
      return;
    }

    const nextPlaylist = [...this.state.playlist, videoId];
    const timestamp = nowIso();

    this.commit({
      ...this.state,
      playlist: nextPlaylist,
      playlistClientUpdatedAt: timestamp,
    });

    this.bridges?.enqueuePlaylist({
      orderedVideoIds: nextPlaylist,
      clientUpdatedAt: timestamp,
    });
  }

  removeFromPlaylist(id: string): void {
    if (!this.state.playlist.includes(id)) {
      return;
    }

    const nextPlaylist = this.state.playlist.filter(
      (existing) => existing !== id,
    );
    const timestamp = nowIso();

    this.commit({
      ...this.state,
      playlist: nextPlaylist,
      playlistClientUpdatedAt: timestamp,
    });

    this.bridges?.enqueuePlaylist({
      orderedVideoIds: nextPlaylist,
      clientUpdatedAt: timestamp,
    });
  }

  addBookmark(video: MobileVideo): void {
    if (this.state.bookmarked.includes(video.id)) {
      return;
    }

    const existing = this.state.items[video.id];
    const merged: MobileVideo = {
      ...video,
      download: existing?.download ?? null,
      watchProgress: existing?.watchProgress ?? video.watchProgress,
      progressClientUpdatedAt:
        existing?.progressClientUpdatedAt ?? video.progressClientUpdatedAt,
      // Don't wipe a playback-learned duration (or its pending-report
      // flag) with the catalog row's null — same-generation rows only.
      durationSeconds:
        video.durationSeconds ??
        (existing != null && keepCachedDuration(existing, video.fileUploadedAt)
          ? existing.durationSeconds
          : null) ??
        null,
      ...(video.durationSeconds == null &&
      existing?.durationSecondsPendingReport === true &&
      existing != null &&
      keepCachedDuration(existing, video.fileUploadedAt)
        ? { durationSecondsPendingReport: true }
        : {}),
      ...(video.durationSeconds == null && existing != null
        ? keptNewerStamp(existing, video.fileUploadedAt)
        : {}),
    };

    const timestamp = nowIso();

    this.commit({
      ...this.state,
      items: { ...this.state.items, [video.id]: merged },
      bookmarked: [...this.state.bookmarked, video.id],
      bookmarkClientUpdatedAt: {
        ...this.state.bookmarkClientUpdatedAt,
        [video.id]: timestamp,
      },
    });

    this.bridges?.enqueueBookmark({
      videoId: video.id,
      bookmarked: true,
      clientUpdatedAt: timestamp,
    });
  }

  removeBookmark(id: string): void {
    if (!this.state.bookmarked.includes(id)) {
      return;
    }

    const timestamp = nowIso();

    this.commit({
      ...this.state,
      bookmarked: this.state.bookmarked.filter((existing) => existing !== id),
      bookmarkClientUpdatedAt: {
        ...this.state.bookmarkClientUpdatedAt,
        [id]: timestamp,
      },
    });

    this.bridges?.enqueueBookmark({
      videoId: id,
      bookmarked: false,
      clientUpdatedAt: timestamp,
    });
  }

  // Id-only bookmark toggle for callers that only have a videoId (web page
  // save buttons). Membership + LWW + sync; `addBookmark(video)` is the
  // metadata-carrying variant used where a full MobileVideo is available.
  setVideoBookmarkById(videoId: string, bookmarked: boolean): void {
    const already = this.state.bookmarked.includes(videoId);

    if (already === bookmarked) {
      return;
    }

    const timestamp = nowIso();
    const nextBookmarked = bookmarked
      ? [...this.state.bookmarked, videoId]
      : this.state.bookmarked.filter((existing) => existing !== videoId);

    this.commit({
      ...this.state,
      bookmarked: nextBookmarked,
      bookmarkClientUpdatedAt: {
        ...this.state.bookmarkClientUpdatedAt,
        [videoId]: timestamp,
      },
    });

    this.bridges?.enqueueBookmark({
      videoId,
      bookmarked,
      clientUpdatedAt: timestamp,
    });
  }

  bookmarkedInOrder(roundId: string): MobileVideo[] {
    const result: MobileVideo[] = [];

    for (const id of this.state.bookmarked) {
      const item = this.state.items[id];

      if (item && item.roundId === roundId) {
        result.push(item);
      }
    }

    return result;
  }

  // Applies the caller's ordering to the playlist. Callers reorder the list
  // they render — under a roundId that's only the current round's loaded
  // entries — so the reordered subset is spliced back into the full order:
  // entries the caller doesn't show (other rounds', or ids the catalog
  // hasn't supplied yet) keep their original slots. A reorder gesture must
  // never delete queue entries — the sync is a whole-list LWW replace, so a
  // dropped id would be tombstoned on every device. Ids that were never in
  // the playlist are ignored.
  reorderPlaylist(orderedIds: string[]): void {
    const known = new Set(this.state.playlist);
    const reorderedVisible = orderedIds.filter((id) => known.has(id));
    const visible = new Set(reorderedVisible);
    let nextVisible = 0;

    const next = this.state.playlist.map((id) =>
      visible.has(id) ? reorderedVisible[nextVisible++] : id,
    );

    const timestamp = nowIso();

    this.commit({
      ...this.state,
      playlist: next,
      playlistClientUpdatedAt: timestamp,
    });

    this.bridges?.enqueuePlaylist({
      orderedVideoIds: next,
      clientUpdatedAt: timestamp,
    });
  }

  clearDownloadsForRound(roundId: string): void {
    const items: Record<string, MobileVideo> = {};
    const clearedIds = new Set<string>();

    for (const [id, item] of Object.entries(this.state.items)) {
      if (item.roundId === roundId) {
        items[id] = {
          ...item,
          download: null,
          transcriptContent: null,
          chapters: null,
        };
        clearedIds.add(id);
      } else {
        items[id] = item;
      }
    }

    // The cleared rows stay listed in the blob, so their side records must
    // not survive to rejoin them on a later hydrate. Deleting up front would
    // destroy the media copy of rows a FAILED persist resurrects — instead
    // the pending-deletes list rides in the same blob write as the clear
    // (whenever the cleared rows become durable, so does the cleanup order),
    // and records are deleted only after that write confirms. Whatever the
    // failure interleaving, hydrate either sees the old rows with their
    // records intact, or the cleared rows with the cleanup instruction.
    const persisted = this.commit({
      ...this.state,
      items,
      order: this.state.order.filter((id) => !clearedIds.has(id)),
      pendingMediaCacheDeletes: [
        ...new Set([...this.state.pendingMediaCacheDeletes, ...clearedIds]),
      ],
    });

    for (const id of clearedIds) {
      this.mediaSideRecorded.delete(id);
    }

    if (!persisted) {
      return;
    }

    const wipeToken = {};

    for (const id of clearedIds) {
      this.pendingDeleteStamps.set(id, wipeToken);
    }

    // Tracked under a synthetic queue key (":" is outside the nanoid
    // alphabet, so it can never collide with a video id) so
    // whenMediaCacheIdle covers the unlisting commit too, not just the
    // per-id removes it waits on.
    void this.queueMediaOp(`round-clear:${roundId}`, async () => {
      const results = await Promise.all(
        [...clearedIds].map(async (id) => {
          const ok = await this.queueMediaOp(id, async () => {
            // Revalidate at RUN time: a re-cache after this wipe queued its
            // cleanup drops/replaces the stamp and enters its fresh write
            // ahead of this delete in the id's chain — deleting then would
            // destroy a newly written, already-licensed record. A stale
            // claim skips; the fresh cache owns the record now.
            if (this.pendingDeleteStamps.get(id) !== wipeToken) {
              return false;
            }

            return this.removeSideRecord(id);
          });
          this.dropLegacyCopy(id);
          return { id, ok };
        }),
      );
      // Unlist only entries that still belong to THIS wipe's generation: a
      // re-cache + re-wipe during the deletes re-stamps the id, and that
      // newer instruction must survive this wipe's bookkeeping.
      const deleted = new Set(
        results
          .filter(
            (r) => r.ok && this.pendingDeleteStamps.get(r.id) === wipeToken,
          )
          .map((r) => r.id),
      );

      for (const id of deleted) {
        this.pendingDeleteStamps.delete(id);
      }

      if (deleted.size > 0) {
        // Unlist the completed deletions. Best-effort: if THIS write fails,
        // the records are already gone, and hydrate's re-delete of a missing
        // record is a no-op that then unlists them. Filter against the
        // CURRENT list — a fresh cache written for one of these ids during
        // the deletes has already unlisted it (commitMediaUpdate), and that
        // unlisting must not be undone here.
        this.commit({
          ...this.state,
          pendingMediaCacheDeletes: this.state.pendingMediaCacheDeletes.filter(
            (id) => !deleted.has(id),
          ),
        });
      }
    });
  }

  setTranscriptContent(id: string, content: string): void {
    const existing = this.state.items[id];

    if (!existing) {
      return;
    }

    if (existing.transcriptContent === content) {
      return;
    }

    this.commitMediaUpdate(id, {
      ...existing,
      transcriptContent: content,
    });
  }

  setChapters(id: string, chapters: MobileChapter[]): void {
    const existing = this.state.items[id];

    if (!existing) {
      return;
    }

    if (chaptersEqual(existing.chapters, chapters)) {
      return;
    }

    this.commitMediaUpdate(id, { ...existing, chapters });
  }

  // `excludeIds`: items the caller already has mid-handoff. A queued item can
  // be one of those — something re-queued it while its startDownload was
  // still awaiting the signed URL — and returning it would double-start it.
  pickNextRunnable(excludeIds?: ReadonlySet<string>): MobileVideo | null {
    for (const id of this.state.order) {
      if (excludeIds?.has(id)) {
        continue;
      }

      const item = this.state.items[id];

      if (item?.download?.status === "queued") {
        return item;
      }
    }

    return null;
  }

  // The downloads actually moving bytes — at most the plugin's five transfer
  // slots, not the whole handed-off queue. Deliberately NOT a "runningItems"
  // list: the only caller (the navigation hold) wants live transfers, and a
  // list of every "running" record hands it the entire round.
  transferringItems(): MobileVideo[] {
    const result: MobileVideo[] = [];

    for (const id of this.state.order) {
      const item = this.state.items[id];

      if (item != null && isActivelyDownloading(item.download)) {
        result.push(item);
      }
    }

    return result;
  }

  itemsInOrder(): MobileVideo[] {
    const result: MobileVideo[] = [];

    for (const id of this.state.order) {
      const item = this.state.items[id];

      if (item) {
        result.push(item);
      }
    }

    return result;
  }

  // The view getters below require a roundId because the persisted store is
  // multi-round and never pruned, while the server's video state is
  // round-scoped: rows from a round the app no longer points at get no
  // updates and no tombstones, so they sit in the local store forever. A
  // round-scoped view keeps every client — long-lived phone, fresh install,
  // web queue drawer — rendering the same lists (SP-2511).
  playlistInOrder(roundId: string): MobileVideo[] {
    const result: MobileVideo[] = [];

    for (const id of this.state.playlist) {
      const item = this.state.items[id];

      if (item && item.roundId === roundId) {
        result.push(item);
      }
    }

    return result;
  }

  // Every item that carries a progress timestamp, most recently played
  // first — the shared source for "Recent" and "Continue watching", which
  // differ only in how they filter by progress.
  itemsByProgressRecency(roundId: string): MobileVideo[] {
    return Object.values(this.state.items)
      .filter((item) => item.roundId === roundId)
      .sort((a, b) =>
        (b.progressClientUpdatedAt ?? "").localeCompare(
          a.progressClientUpdatedAt ?? "",
        ),
      );
  }

  // The queue entry that should play once the video `id` finishes: ANY
  // finishing video continues into the queue. The walk starts after `id`'s
  // queue position and wraps around to the head, so ending the last queued
  // video continues with what's left at the top; a video that was never
  // queued (e.g. played straight from the round list) starts the queue from
  // the head. Walks past ids with no cached metadata yet, videos deleted
  // server-side, and — when `roundId` is given — other rounds' entries, since
  // advancing into any of those would just bounce off the detail page (it
  // resolves against the live catalog), kicking the user out of the player on
  // every hand-off. Returns null when nothing playable is queued.
  nextPlayableInQueue(id: string, roundId?: string): MobileVideo | null {
    const { playlist, items } = this.state;
    const index = playlist.indexOf(id);
    const start = index === -1 ? 0 : index + 1;

    for (let offset = 0; offset < playlist.length; offset++) {
      const candidateId = playlist[(start + offset) % playlist.length];

      if (candidateId === id) {
        continue;
      }

      const next = items[candidateId];

      if (next == null || isRemovedFromCatalog(next)) {
        continue;
      }

      if (roundId != null && next.roundId !== roundId) {
        continue;
      }

      return next;
    }

    return null;
  }
}
